import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { listarAplicaciones, anularAplicacion } from '../../services/aplicaciones';
import { useAuth } from '../../hooks/useAuth';
import { listPendingAplicacionesByUser } from '../../services/offline/offlineDb';
import { trySync, isSyncing } from '../../services/offline/syncQueue';
import { isOnline } from '../../services/offline/networkStatus';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import AppShell from '../../components/layout/AppShell';
import Button from '../../components/ui/Button';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';
import Icon from '../../components/ui/Icon';
import AplicacionRowActions from '../../components/aplicaciones/AplicacionRowActions';
import AnularAplicacionModal from '../../components/aplicaciones/AnularAplicacionModal';

const OFFLINE_MESSAGE = 'Necesitas conexión a Internet para modificar una aplicación.';

const FILTROS = [
  { value: 'ACTIVA', label: 'Activas' },
  { value: 'ANULADA', label: 'Anuladas' },
  { value: 'TODAS', label: 'Todas' },
];

const SYNC_LABEL = {
  PENDIENTE: 'PENDIENTE DE SINCRONIZAR',
  SINCRONIZANDO: 'SINCRONIZANDO',
  ERROR: 'ERROR AL SINCRONIZAR',
};

function formatFecha(fechaaplicacion) {
  if (!fechaaplicacion) return '—';
  try {
    return new Date(`${fechaaplicacion}T00:00:00`).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return fechaaplicacion;
  }
}

function formatHa(ha) {
  if (ha === null || ha === undefined || ha === '') return '—';
  return `${Number(ha).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ha`;
}

// Un registro pendiente local no tiene id de servidor todavía: se adapta al
// mismo shape que usan las tarjetas/filas para poder mezclarlos en un solo
// listado, marcado con isLocal para que la UI sepa que no debe tratarlo
// como si ya estuviera guardado en el servidor (sin click a detalle, sin
// editar/eliminar — eso llega en una fase posterior).
function pendienteToRow(item) {
  return {
    id: `local-${item.client_operation_id}`,
    isLocal: true,
    clientOperationId: item.client_operation_id,
    fechaaplicacion: item.payload?.fechaaplicacion,
    huerto: item.payload?.huerto,
    cuartel: item.payload?.cuartel,
    ha: item.payload?.ha,
    cantidadProductos: Array.isArray(item.payload?.productos) ? item.payload.productos.length : 0,
    syncLabel: SYNC_LABEL[item.estado_local] || SYNC_LABEL.PENDIENTE,
    errorMessage: item.error_message,
  };
}

function AplicacionesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const online = useNetworkStatus();
  const justSavedOffline = Boolean(location.state?.justSavedOffline);

  const [aplicaciones, setAplicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('ACTIVA');

  const [pendientes, setPendientes] = useState([]);
  const [syncingNow, setSyncingNow] = useState(false);

  const [anularTarget, setAnularTarget] = useState(null);
  const [anulando, setAnulando] = useState(false);
  const [anularError, setAnularError] = useState('');

  const cargar = useCallback(() => {
    setLoading(true);
    listarAplicaciones().then((result) => {
      if (result.ok) {
        setAplicaciones(result.aplicaciones);
      } else {
        setError(result.message);
      }
      setLoading(false);
    });
  }, []);

  const cargarPendientes = useCallback(() => {
    if (!user?.id) return;
    listPendingAplicacionesByUser(user.id).then(setPendientes);
  }, [user?.id]);

  useEffect(() => {
    cargar();
    cargarPendientes();
  }, [cargar, cargarPendientes]);

  // La cola offline puede cambiar por causas externas a esta página
  // (sincronización automática al volver la conexión, al abrir la app, al
  // volver a foreground) — syncQueue avisa con este evento para refrescar.
  useEffect(() => {
    function handleOfflineChange() {
      cargarPendientes();
      setSyncingNow(isSyncing());
      // Si algo terminó de sincronizarse, refresca también el listado de
      // servidor para que la aplicación recién sincronizada aparezca.
      cargar();
    }
    window.addEventListener('fg-offline-change', handleOfflineChange);
    return () => window.removeEventListener('fg-offline-change', handleOfflineChange);
  }, [cargar, cargarPendientes]);

  const handleSincronizarClick = async () => {
    if (!user?.id) return;
    setSyncingNow(true);
    await trySync(user.id);
    setSyncingNow(false);
  };

  const filtradasServidor = useMemo(
    () => aplicaciones.filter((a) => (filtro === 'TODAS' ? true : a.estado === filtro)),
    [aplicaciones, filtro]
  );

  // Los pendientes locales no son ni ACTIVA ni ANULADA (estado de servidor):
  // se muestran junto a las activas y en "Todas", nunca en el filtro
  // "Anuladas".
  const pendientesVisibles = filtro === 'ANULADA' ? [] : pendientes.map(pendienteToRow);
  const filtradas = [...pendientesVisibles, ...filtradasServidor];

  const handleEditClick = (event, id) => {
    event.stopPropagation();
    navigate(`/aplicaciones/${id}/editar`);
  };

  const handleEliminarClick = (event, aplicacion) => {
    event.stopPropagation();
    setAnularError('');
    setAnularTarget(aplicacion);
  };

  const handleConfirmarAnular = async (motivo) => {
    if (!anularTarget) return;

    if (!isOnline()) {
      setAnularError(OFFLINE_MESSAGE);
      return;
    }

    setAnulando(true);
    setAnularError('');

    const result = await anularAplicacion(anularTarget.id, motivo);

    setAnulando(false);

    if (!result.ok) {
      setAnularError(result.networkError ? OFFLINE_MESSAGE : result.message);
      return;
    }

    setAplicaciones((prev) =>
      prev.map((a) => (a.id === anularTarget.id ? { ...a, estado: 'ANULADA' } : a))
    );
    setAnularTarget(null);
  };

  return (
    <AppShell title="Aplicaciones">
      <div className="flex flex-col gap-6">
        {justSavedOffline && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-center text-sm font-medium text-amber-700">
            Sin conexión: la aplicación quedó guardada en tu teléfono y se sincronizará automáticamente.
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-bold text-gray-900">Aplicaciones</h1>
          <Button className="sm:w-auto sm:px-6" onClick={() => navigate('/aplicaciones/nueva')}>
            <Icon name="plus" className="mr-1.5 h-5 w-5" />
            Nueva aplicación
          </Button>
        </div>

        {pendientes.length > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-medium text-amber-800">
              {pendientes.length} pendiente{pendientes.length === 1 ? '' : 's'} de sincronizar
            </p>
            <button
              type="button"
              onClick={handleSincronizarClick}
              disabled={syncingNow || !online}
              className="shrink-0 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-amber-800 shadow-sm transition-colors hover:bg-amber-100 disabled:opacity-60"
            >
              {!online ? 'Esperando conexión...' : syncingNow ? 'Sincronizando...' : 'Sincronizar'}
            </button>
          </div>
        )}

        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2 whitespace-nowrap">
            {FILTROS.map((filtroOpcion) => (
              <button
                key={filtroOpcion.value}
                type="button"
                onClick={() => setFiltro(filtroOpcion.value)}
                className={`min-h-[40px] shrink-0 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 ${
                  filtro === filtroOpcion.value
                    ? 'border border-green-200 bg-green-50 text-green-700'
                    : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {filtroOpcion.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-700">{error}</p>}

        {loading ? (
          <p className="text-center text-sm text-gray-500">Cargando...</p>
        ) : filtradas.length === 0 ? (
          <EmptyState message="No hay aplicaciones en este filtro." />
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-2xl border border-gray-100 bg-white lg:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Fecha</th>
                    <th className="px-4 py-3 font-medium">Ubicación</th>
                    <th className="px-4 py-3 font-medium">Sup.</th>
                    <th className="px-4 py-3 font-medium">Productos</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                    <th className="px-4 py-3 text-right font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtradas.map((aplicacion) => (
                    <tr
                      key={aplicacion.id}
                      onClick={() => !aplicacion.isLocal && navigate(`/aplicaciones/${aplicacion.id}`)}
                      className={`transition-colors ${
                        aplicacion.isLocal ? '' : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3 text-gray-900">{formatFecha(aplicacion.fechaaplicacion)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {aplicacion.huerto || '—'} · {aplicacion.cuartel || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{formatHa(aplicacion.ha)}</td>
                      <td className="px-4 py-3 text-gray-600">{aplicacion.cantidadProductos}</td>
                      <td className="px-4 py-3">
                        <StatusBadge estado={aplicacion.isLocal ? aplicacion.syncLabel : aplicacion.estado} />
                      </td>
                      <td className="px-4 py-3">
                        {!aplicacion.isLocal && aplicacion.estado === 'ACTIVA' && (
                          <div className="flex justify-end">
                            <AplicacionRowActions
                              onEdit={(event) => handleEditClick(event, aplicacion.id)}
                              onEliminar={(event) => handleEliminarClick(event, aplicacion)}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 lg:hidden">
              {filtradas.map((aplicacion) => (
                <div
                  key={aplicacion.id}
                  onClick={() => !aplicacion.isLocal && navigate(`/aplicaciones/${aplicacion.id}`)}
                  role={aplicacion.isLocal ? undefined : 'button'}
                  tabIndex={aplicacion.isLocal ? undefined : 0}
                  onKeyDown={(event) => {
                    if (!aplicacion.isLocal && event.key === 'Enter') navigate(`/aplicaciones/${aplicacion.id}`);
                  }}
                  className={`rounded-2xl border bg-white p-4 shadow-sm transition-colors ${
                    aplicacion.isLocal
                      ? 'border-amber-200'
                      : 'cursor-pointer border-gray-100 hover:border-green-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-gray-500">{formatFecha(aplicacion.fechaaplicacion)}</p>
                    <StatusBadge estado={aplicacion.isLocal ? aplicacion.syncLabel : aplicacion.estado} />
                  </div>

                  <p className="mt-2 font-semibold text-gray-900">{aplicacion.huerto || '—'}</p>
                  <p className="text-sm text-gray-500">{aplicacion.cuartel || '—'}</p>

                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-gray-400">
                      {formatHa(aplicacion.ha)} · {aplicacion.cantidadProductos} producto
                      {aplicacion.cantidadProductos === 1 ? '' : 's'}
                    </p>

                    {!aplicacion.isLocal && aplicacion.estado === 'ACTIVA' && (
                      <AplicacionRowActions
                        onEdit={(event) => handleEditClick(event, aplicacion.id)}
                        onEliminar={(event) => handleEliminarClick(event, aplicacion)}
                      />
                    )}
                  </div>

                  {aplicacion.isLocal && aplicacion.errorMessage && (
                    <p className="mt-2 text-xs text-red-600">{aplicacion.errorMessage}</p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <AnularAplicacionModal
        open={Boolean(anularTarget)}
        aplicacion={anularTarget}
        confirming={anulando}
        error={anularError}
        onConfirm={handleConfirmarAnular}
        onCancel={() => {
          setAnularTarget(null);
          setAnularError('');
        }}
      />
    </AppShell>
  );
}

export default AplicacionesPage;
